import { Pagination } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { CiEdit } from "react-icons/ci";
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';
import SearchBox from '../Common-Components/SearchBox';
import '../common-css/Table.css';
import { deleteItem, showTableResponse } from '../common-helpers/commonServices';
import { sortNeetTopics, sortNeetList } from './neetSortHelper';
import NEET_TopicModal from './NEET_TopicModal';
import { MdDeleteOutline } from 'react-icons/md';

const NEET_Topics = () => {
    const childRef = useRef();
    const [sections, setSections] = useState([]); // all topics from API
    const [checked, setChecked] = useState([]);
    const [subjectsList, setSubjectsList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadSubjects, setSubjectLoading] = useState(false);
    const [topicSortDirection, setTopicSortDirection] = useState('asc');

    const handleTopicSortToggle = () => {
        const nextDirection = topicSortDirection === 'asc' ? 'desc' : 'asc';
        setTopicSortDirection(nextDirection);
        setChecked(prev => sortNeetTopics(prev, nextDirection));
        setSections(prev => sortNeetTopics(prev, nextDirection));
    };

    // react-select styling
    const selectStyles = {
        control: (provided) => ({
            ...provided,
            width: '100%',
            backgroundColor: 'transparent',
            borderColor: '#17a2b8',
        }),
    };

    // Fetch all topics
    useEffect(() => {
        axios.get(BACKEND_SERVER_URL + '/neet/topics')
            .then((res) => {
                const topics = sortNeetTopics(res.data.topics || [], topicSortDirection);
                setSections(topics);
                setChecked(topics);

                // prepare subject dropdown (unique subjects)
                const uniqueSubjects = [...new Map(topics.map(t => [t.SubjectID, { value: t.SubjectID, label: t.Subject }])).values()];
                setSubjectsList(sortNeetList(uniqueSubjects, 'label'));
            })
            .catch((error) => {
                console.log(error);
                toast.error(error.response?.data?.message || "Error fetching topics");
            });
    }, [loadSubjects]);

    // When subject changes → set section dropdown
    useEffect(() => {
        if (selectedSubject) {
            const filteredSections = sections
                .filter(t => t.SubjectID === selectedSubject.value)
                .map(t => ({ value: t.SectionID, label: t.Section }));

            // remove duplicates and sort naturally
            const uniqueSections = [...new Map(filteredSections.map(s => [s.value, s])).values()];
            setSectionsList(sortNeetList(uniqueSections, 'label'));
            setSelectedSection(null); // reset section
        } else {
            setSectionsList([]);
            setSelectedSection(null);
        }
    }, [selectedSubject, sections]);

    // Filter topics based on subject & section
    useEffect(() => {
        let filtered = sections;

        if (selectedSubject) {
            filtered = filtered.filter(t => t.SubjectID === selectedSubject.value);
        }
        if (selectedSection) {
            filtered = filtered.filter(t => t.SectionID === selectedSection.value);
        }

        setChecked(sortNeetTopics(filtered, topicSortDirection));
        setCurrentPage(1); // reset to first page
    }, [selectedSubject, selectedSection, sections, topicSortDirection]);

    // Pagination
    const pageSize = 50;
    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;
    const records = checked?.slice(firstIndex, lastIndex);
    const npage = Math.ceil(checked?.length / pageSize);
    const number = [...Array(npage + 1).keys()].slice(1);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="page-title p-2 mb-2">
                                <div className="row w-100 d-flex justify-content-between">
                                    <div className='col-sm-8 d-flex justify-content-between'>
                                        <div className="col-2">
                                            <button type="button" className="btn btn-dark btn-sm"
                                                onClick={() => childRef.current.openModal()}>
                                                + Add
                                            </button>
                                        </div>
                                        <div className="col-10 d-flex justify-content-start align-items-center">
                                            <div className="col-5 pe-2">
                                                <Select
                                                    value={selectedSubject}
                                                    onChange={setSelectedSubject}
                                                    options={subjectsList}
                                                    placeholder="Select Subject"
                                                    styles={selectStyles}
                                                    isClearable
                                                />
                                            </div>
                                            <div className="col-5">
                                                <Select
                                                    value={selectedSection}
                                                    onChange={setSelectedSection}
                                                    options={sectionsList}
                                                    placeholder="Select Section"
                                                    styles={selectStyles}
                                                    isClearable
                                                    isDisabled={!selectedSubject}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 input-group search-area mb-md-0 mb-3">
                                        <SearchBox setChecked={setChecked} dataArray={sections} searchingProperty={'Topic'} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-12 wow fadeInUp" data-wow-delay="1.5s">
                            <div className="d-sm-flex text-center justify-content-between align-items-center">
                                <div className='dataTables_info text-dark'>Records: {checked.length}</div>
                                {checked.length > pageSize && (
                                    <div className="dataTables_paginate paging_simple_numbers justify-content-center mb-0 pt-2">
                                        <Pagination
                                            count={number.length}
                                            variant="outlined"
                                            color='primary'
                                            size="small"
                                            page={currentPage}
                                            siblingCount={1}
                                            boundaryCount={2}
                                            onChange={(e, p) => setCurrentPage(parseInt(p))}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="table-responsive full-data" style={{ paddingBottom: "1px", transform: 'rotateX(180deg)' }}>
                                <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
                                    <table className="table-responsive-lg table display dataTablesCard student-tab dataTable no-footer mt-0 mb-0" style={{ transform: 'rotateX(180deg)' }}>
                                        <thead style={{ backgroundColor: "#cccccc" }}>
                                            <tr className='text-center border border-left'>
                                                <th 
                                                    className='custom-table-header p-2 text-start'
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={handleTopicSortToggle}
                                                    title="Click to toggle Topic Natural Sorting (Ascending / Descending)"
                                                >
                                                    TOPICS <span style={{ fontSize: '11px', marginLeft: '4px' }}>{topicSortDirection === 'asc' ? '▲' : '▼'}</span>
                                                </th>
                                                <th className='custom-table-header p-2 text-start'>SECTIONS</th>
                                                <th className='custom-table-header p-2 ps-3'>SUBJECTS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.length > 0 ? records.map((item, ind) => (
                                                <tr key={ind} className={`${ind % 2 === 1 ? 'custom row-height' : 'row-height'}`}>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between">
                                                            <h6 className='mb-0 ps-3' style={{ fontSize: '13px', fontWeight: 'bold' }}>
                                                                {item.Topic}
                                                                <CiEdit
                                                                    cursor='pointer'
                                                                    title='Edit Subject'
                                                                    size={18}
                                                                    className='ms-3'
                                                                    onClick={() => childRef.current.openModal(item)}
                                                                />
                                                                <MdDeleteOutline color="red" title='Delete Topic' cursor='pointer' size={18} className='ms-2' onClick={() => { deleteItem(item.Sn, 'Topic', '/neet/deleteTopic/', setSubjectLoading, loadSubjects) }}
                                                                />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1 text-start' style={{ border: "1px solid #cccccc", width: "65px" }}>
                                                        <h6 className="mb-0">{item.Section}</h6>
                                                    </td>
                                                    <td className='p-1 text-start' style={{ border: "1px solid #cccccc", width: "65px" }}>
                                                        <h6 className="mb-0">{item.Subject}</h6>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center">
                                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                                                            {showTableResponse(sections, checked)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <NEET_TopicModal ref={childRef} loadSubjects={loadSubjects} setSubjectLoading={setSubjectLoading} />
        </>
    );
};

export default NEET_Topics;
